/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

import {Box, Checkbox, List, ListItem} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {REFINEMENT_LEGEND} from '../../product-list'
import {CheckIcon} from '../../../components/custom-icons'

// Constants
import {IS_FAMILY_F, IS_FAMILY_MD, IS_FAMILY_FS, IS_FAMILY_NO} from '../../../constants'

/**
 * CheckboxRefinements is a React component that renders a list of checkbox options for filtering or refining a product list.
 *
 * This component is typically used in product listing pages to allow users to select multiple filter criteria, such as brands, sizes, or other attributes.
 * It manages the checked state of each option and triggers callbacks when selections change, enabling dynamic updates to the filtered product results.
 *
 * @component
 * @example
 * <CheckboxRefinements
 *   options={[{ label: 'Brand A', value: 'brand-a' }, { label: 'Brand B', value: 'brand-b' }]}
 *   selectedValues={['brand-a']}
 *   onChange={handleRefinementChange}
 * />
 *
 * @prop {Array<Object>} options - An array of objects representing the available checkbox options. Each object should have a `label` and a `value`.
 * @prop {Array<string>} selectedValues - An array of values indicating which options are currently selected.
 * @prop {function} onChange - Callback function invoked when the selection changes. Receives the updated array of selected values.
 */
const CheckboxRefinements = ({filter, selectedFilters, onSelect, onDeselect}) => {
    const {formatMessage} = useIntl()

    // Define constants for label IDs and default messages
    const LABELS = {
        [IS_FAMILY_F]: {
            id: 'family_filter.isfamily.f.label',
            defaultMessage: 'Family Matching'
        },
        [IS_FAMILY_MD]: {
            id: 'family_filter.isfamily.md.label',
            defaultMessage: 'Mother & Daughter'
        },
        [IS_FAMILY_FS]: {
            id: 'family_filter.isfamily.fs.label',
            defaultMessage: 'Father & Sons'
        }
    }

    // Workaround to display translated display values for c_orliweb_isFamily values
    let adjustedFilter = filter
    if (filter?.attributeId === 'c_orliweb_isFamily' && filter.values) {
        adjustedFilter.values = filter.values
            .filter((filterValue) => filterValue.value !== IS_FAMILY_NO)
            .map((filterValue) => {
                const labelInfo = LABELS[filterValue.value]
                if (labelInfo) {
                    return {
                        ...filterValue,
                        label: formatMessage(labelInfo)
                    }
                }
                return filterValue // Return the original filterValue if no label is found
            })
    }

    return (
        adjustedFilter?.values?.length > 0 && (
            <Box as="fieldset">
                {/* ADA legend */}
                <legend className="sr-only">
                    {formatMessage(REFINEMENT_LEGEND, {label: filter.label})}
                </legend>

                <List display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={10}>
                    {filter.values?.map((value) => {
                        const isChecked = selectedFilters.includes(value.value)
                        // Don't display refinements with no results, unless we got there by selecting too
                        // many refinements
                        if (value.hitCount === 0 && !isChecked) return

                        return (
                            <ListItem key={value.value} margin={0}>
                                <Checkbox
                                    isChecked={isChecked}
                                    icon={<CheckIcon boxSize={'10px'} />}
                                    onChange={() => {
                                        if (isChecked) {
                                            onDeselect(value.value)
                                        } else {
                                            onSelect(value.value)
                                        }
                                    }}
                                    aria-label={formatMessage(
                                        isChecked ? REMOVE_FILTER : ADD_FILTER,
                                        value
                                    )}
                                >
                                    {value.label}
                                </Checkbox>
                            </ListItem>
                        )
                    })}
                </List>
            </Box>
        )
    )
}

CheckboxRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.array,
    onSelect: PropTypes.func,
    onDeselect: PropTypes.func
}

// Memoized CheckboxRefinements to prevent unnecessary re-renders.
// Use case: accordion open / close with CheckboxRefinements
export default React.memo(CheckboxRefinements)
